import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { create, isPlayerSupported } from 'amazon-ivs-player';
import { useTranslate } from '@refinedev/core';

interface IVSPlayerProps {
  streamUrl: string | null;
  isLive?: boolean;
  onPlayerLoad?: () => void;
  onPlayerError?: (error: string) => void;
  onPlayerStateChange?: (state: string) => void;
  style?: React.CSSProperties;
  controls?: boolean;
  autoplay?: boolean;
}

export const IVSPlayer: React.FC<IVSPlayerProps> = ({
  streamUrl,
  isLive = true,
  onPlayerLoad,
  onPlayerError,
  onPlayerStateChange,
  style = {},
  controls = false,
  autoplay = true
}) => {
  const translate = useTranslate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [dots, setDots] = useState('');

  // Stocker les callbacks dans des refs pour éviter les re-renders infinis
  const onPlayerLoadRef = useRef(onPlayerLoad);
  const onPlayerErrorRef = useRef(onPlayerError);
  const onPlayerStateChangeRef = useRef(onPlayerStateChange);

  // Mettre à jour les refs quand les props changent
  useEffect(() => {
    onPlayerLoadRef.current = onPlayerLoad;
  }, [onPlayerLoad]);

  useEffect(() => {
    onPlayerErrorRef.current = onPlayerError;
  }, [onPlayerError]);

  useEffect(() => {
    onPlayerStateChangeRef.current = onPlayerStateChange;
  }, [onPlayerStateChange]);

  // Animation des points de chargement
  useEffect(() => {
    if (isLoading && !isConnected) {
      const interval = setInterval(() => {
        setDots(prev => (prev === '...' ? '' : prev + '.'));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isLoading, isConnected]);

  // Callbacks stables pour les événements IVS (utilisent les refs)
  const handleReady = useCallback(() => {
    setIsLoading(false);
    setError(null);
    onPlayerLoadRef.current?.();
  }, []);

  const handleStateChange = useCallback((event: any) => {
    const state = event.state;
    onPlayerStateChangeRef.current?.(state);

    switch (state) {
      case 'Playing':
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
        break;
      case 'Buffering':
        // Ne pas mettre isLoading à true pour éviter le flickering
        break;
      case 'Idle':
        setIsConnected(false);
        break;
      case 'Ended':
        setIsConnected(false);
        setIsLoading(false);
        break;
    }
  }, []);

  const handleError = useCallback((event: any) => {
    const errorMsg = translate('player.playbackError', { type: event.type });
    setError(errorMsg);
    setIsLoading(false);
    setIsConnected(false);
    onPlayerError?.(errorMsg);
  }, [onPlayerError, translate]);

  // Initialisation du player - dépend UNIQUEMENT de streamUrl et autoplay
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    if (!isPlayerSupported) {
      const errorMsg = translate('player.notSupported');
      setError(errorMsg);
      onPlayerErrorRef.current?.(errorMsg);
      return;
    }

    // Nettoyer le player existant avant d'en créer un nouveau
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.delete();
      } catch (e) {
        // Ignorer les erreurs de nettoyage
      }
      playerRef.current = null;
    }

    // Ne créer le player que si on a une URL valide
    if (!streamUrl) {
      setIsLoading(false);
      return;
    }

    try {
      // Réinitialiser l'état
      setIsLoading(true);
      setError(null);
      setIsConnected(false);

      // Création du player IVS
      const player = create({
        wasmWorker: '/amazon-ivs-wasmworker.min.js',
        wasmBinary: '/amazon-ivs-wasmworker.min.wasm'
      });

      playerRef.current = player;

      // Attachement au élément vidéo
      player.attachHTMLVideoElement(videoRef.current);

      // Configuration du player
      player.setAutoplay(autoplay);
      player.setMuted(false);
      player.setVolume(0.8);

      // Gestionnaires d'événements
      player.addEventListener('PlayerState' as any, handleStateChange);
      player.addEventListener('Error' as any, handleError);
      player.addEventListener('Ready' as any, handleReady);

      // Chargement de l'URL avec un léger délai pour laisser le player s'initialiser
      setTimeout(() => {
        if (playerRef.current) {
          player.load(streamUrl);
        }
      }, 100);

    } catch (err) {
      const unknownError = translate('player.unknownError');
      setError(err instanceof Error ? err.message : unknownError);
      onPlayerError?.(err instanceof Error ? err.message : unknownError);
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.pause();
          playerRef.current.delete();
        } catch (e) {
          // Ignorer les erreurs de nettoyage
        }
        playerRef.current = null;
      }
    };
    // Note: handleStateChange, handleError, handleReady sont stables (useCallback avec [])
    // donc on ne les inclut pas dans les dépendances pour éviter les re-créations inutiles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamUrl, autoplay]);

  const handleRetry = useCallback(() => {
    if (retryCount < 3 && streamUrl) {
      setRetryCount(prev => prev + 1);
      setError(null);
      setIsLoading(true);
      setIsConnected(false);

      if (playerRef.current) {
        playerRef.current.load(streamUrl);
      }
    }
  }, [retryCount, streamUrl]);

  const defaultStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    borderRadius: '10px',
    position: 'relative',
    overflow: 'hidden',
    ...style
  };

  if (error) {
    return (
      <Box sx={defaultStyle}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            padding: 2
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {translate('player.connectionError')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#ccc' }}>
            {error}
          </Typography>
          {retryCount < 3 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleRetry}
              sx={{ backgroundColor: '#333' }}
            >
              {translate('player.retry', { current: retryCount, max: 3 })}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={defaultStyle}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        playsInline
        controls={controls}
      />

      {(isLoading && !isConnected) && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white',
            zIndex: 10
          }}
        >
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6">
            {translate('player.connecting')}{dots}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ccc', mt: 1 }}>
            {translate('player.loadingStream')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
